import React, { useState, useEffect, useMemo } from 'react';
import { db, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { ConventionMetadata, UserProfile } from '../../types';
import { cleanIcsString } from '../../lib/utils';
import ConList from './ConList';
import ConWizard from './ConWizard';
// Removed ConSessions
import ConAdminPanel from './ConAdminPanel';
import ConVolunteerForm from './ConVolunteerForm';
import { isSystemAdmin } from '../../lib/admin';
import { Calendar, Database, Plus, ChevronLeft, Package, Layout, Share2, Copy } from 'lucide-react';
import { toast } from 'sonner';

interface ConDashboardProps {
  currentUserProfile: UserProfile | null;
  initialConId?: string | null;
  onClearInitialCon?: () => void;
  isPublicMode?: boolean;
}

type ViewMode = 'list' | 'wizard' | 'props' | 'inventory' | 'admin' | 'volunteer';

export default function ConDashboard({ currentUserProfile, initialConId, onClearInitialCon, isPublicMode = false }: ConDashboardProps) {
  const [view, setView] = useState<ViewMode>('list');
  const [selectedConId, setSelectedConId] = useState<string | null>(null);
  const [conventions, setConventions] = useState<ConventionMetadata[]>([]);
  const [eventsAsCons, setEventsAsCons] = useState<ConventionMetadata[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = isSystemAdmin(currentUserProfile);
  const canCreateCon = currentUserProfile?.roles?.some(r => ['admin', 'user'].includes(r)) || isAdmin;

  useEffect(() => {
    // Admins see all, users see their own
    const conQuery = isAdmin 
      ? collection(db, 'conventions')
      : query(collection(db, 'conventions'), where('ownerId', '==', currentUserProfile?.uid));

    const unsubCons = onSnapshot(conQuery, (snap) => {
      setConventions(snap.docs.map(d => ({ id: d.id, ...d.data() } as ConventionMetadata)));
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'conventions');
    });

    // Also fetch events of type 'Convention' from Command Deck
    const eventConQuery = query(collection(db, 'events'), where('type', '==', 'Convention'));
    const unsubEvents = onSnapshot(eventConQuery, (snap) => {
      const mappedEvents = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: cleanIcsString(data.name),
          startDate: data.startDate,
          endDate: data.endDate || data.startDate,
          ownerId: 'command-deck', // Marker for command deck events
          status: data.status || 'active',
          dailyHours: data.dailyHours || {},
          address: cleanIcsString(data.location || ''),
          host: data.host,
          commFrequency: data.contact,
          pertinentInfo: cleanIcsString(data.pertinentInfo || ''),
          sessions: data.sessions,
          schedule: data.schedule
        } as ConventionMetadata;
      });
      setEventsAsCons(mappedEvents);
      setLoading(false);
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'events');
    });

    return () => {
      unsubCons();
      unsubEvents();
    };
  }, [isAdmin, currentUserProfile?.uid]);

  const allConventions = useMemo(() => {
    // Merge both and deduplicate by name+date if they are the same
    // Priority to real 'conventions' records if they exist
    const merged = [...conventions];
    
    eventsAsCons.forEach(eventCon => {
      const exists = conventions.some(c => 
        (c.name === eventCon.name && c.startDate.split('T')[0] === eventCon.startDate.split('T')[0]) ||
        c.id === eventCon.id
      );
      if (!exists) {
        merged.push(eventCon);
      }
    });

    // Sort by date ascending (current to future)
    const sorted = merged.sort((a, b) => {
      const dateA = new Date(a.startDate).getTime();
      const dateB = new Date(b.startDate).getTime();
      return dateA - dateB;
    });

    // Filter to only show "current" conventions
    const now = new Date();
    // Set to start of today to include conventions happening today
    now.setHours(0, 0, 0, 0);
    
    return sorted.filter(con => {
      // Exclude explicitly completed conventions
      if (con.status === 'completed') return false;
      
      // Determine the end date of the convention
      const endDateStr = con.endDate || con.startDate;
      if (!endDateStr) return true; // If no date, keep it just in case
      
      const endDate = new Date(endDateStr);
      // Add one day to the end date so it doesn't disappear on the last day
      endDate.setDate(endDate.getDate() + 1);
      
      return endDate >= now;
    });
  }, [conventions, eventsAsCons]);

  useEffect(() => {
    if (initialConId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedConId(initialConId);
      // Determine if admin or volunteer
      const con = allConventions.find(c => c.id === initialConId);
      const isOwner = con?.ownerId === currentUserProfile?.uid || con?.ownerId === 'command-deck';
      
      if (isAdmin || isOwner) {
        setView('admin');
      } else {
        setView('volunteer');
      }
      
      if (onClearInitialCon) onClearInitialCon();
    }
  }, [initialConId, allConventions, isAdmin, currentUserProfile?.uid, onClearInitialCon]);

  const handleSelectCon = (id: string) => {
    setSelectedConId(id);
    setView('admin');
  };

  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Con Planner Header */}
      <div className="bg-lcars-blue text-black p-3 lg:p-4 rounded-r-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shrink-0">
        <div className="flex items-center gap-3 lg:gap-4">
          <Calendar className="w-6 h-6 lg:w-8 lg:h-8" />
          <div>
            <h1 className="text-xl lg:text-2xl font-bold uppercase tracking-tighter leading-tight">Con Duty Roster</h1>
            <p className="text-[0.5rem] lg:text-[0.6rem] font-bold uppercase tracking-widest opacity-60">Strategic Deployment & Management</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          {view !== 'list' && (
            <button 
              onClick={() => setView('list')}
              className="flex items-center gap-1 bg-[var(--background)] text-lcars-blue px-4 py-1.5 rounded-full text-xs font-bold uppercase"
            >
              <ChevronLeft className="w-4 h-4" /> BACK TO LOGS
            </button>
          )}
          {view === 'list' && !isPublicMode && canCreateCon && (
            <button 
              onClick={() => setView('wizard')}
              className="flex items-center gap-2 bg-[var(--background)] text-lcars-blue px-4 py-1.5 rounded-full text-xs font-bold uppercase"
            >
              <Plus className="w-4 h-4" /> INITIALIZE NEW CON
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto pr-4 scrollbar-hide">
        {view === 'list' && (
          <ConList 
            conventions={allConventions} 
            onSelect={handleSelectCon} 
            currentUserUid={currentUserProfile?.uid || ''} 
            isPublicMode={isPublicMode}
          />
        )}

        {view === 'wizard' && (
          <ConWizard 
            onComplete={() => setView('list')} 
            currentUserUid={currentUserProfile?.uid || ''} 
          />
        )}
        {view === 'admin' && selectedConId && (
          <ConAdminPanel 
            conId={selectedConId} 
            onBack={() => setView('list')} 
            currentUserUid={currentUserProfile?.uid || ''} 
            currentUserProfile={currentUserProfile}
          />
        )}
        {view === 'volunteer' && selectedConId && (
          <div className="space-y-4">
             <div className="flex flex-col sm:flex-row gap-4 mb-4">
               <button 
                 onClick={() => setView('list')}
                 className="flex items-center justify-center gap-2 text-lcars-blue font-bold uppercase text-sm bg-white/5 px-4 py-2 hover:bg-white/10 transition-colors"
               >
                 <ChevronLeft className="w-4 h-4" /> Return
               </button>
               <a 
                 href={`${window.location.origin}${window.location.pathname}#/schedule/${selectedConId}`}
                 target="_blank"
                 rel="noopener noreferrer"
                 className="flex items-center justify-center gap-2 text-lcars-tan font-bold uppercase text-sm bg-white/5 px-4 py-2 hover:bg-white/10 transition-colors"
               >
                 <Calendar className="w-4 h-4" /> Public Schedule
               </a>
               <button 
                 onClick={() => {
                   const url = `${window.location.origin}${window.location.pathname}#/signup/${selectedConId}`;
                   navigator.clipboard.writeText(url);
                   toast.success('Link copied to clipboard');
                 }}
                 className="flex items-center justify-center gap-2 text-lcars-gold font-bold uppercase text-sm bg-white/5 px-4 py-2 hover:bg-white/10 transition-colors"
               >
                 <Copy className="w-4 h-4" /> Copy Volunteer Link
               </button>
             </div>
             <ConVolunteerForm conId={selectedConId} currentUserProfile={currentUserProfile} />
          </div>
        )}
      </div>
    </div>
  );
}
